import { FunctionComponent, useMemo } from "react";

import { useTranslation } from "react-i18next";

import {
	faChevronDown,
	faChevronUp,
	faTrash,
	faClock,
	faTriangleExclamation,
	faUserPlus,
	faCircleQuestion,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { Information } from "@/components/Information/Information";
import { ReplyReasoningPopover } from "@/components/ReplyReasoningPopover/ReplyReasoningPopover";
import { classNames } from "@/utils/classNames";

import { Draft, DraftTiming } from "../utils/inboxDraft.types";

import { resolveSenderTier } from "../utils/resolveSenderTier.util";

import { resolveDraftPreviewHtml } from "../utils/resolveDraftPreviewHtml";

import { InviteNoteCharCounter } from "./InviteNoteCharCounter";
import { TimingDropdown } from "./TimingDropdown";
import { SenderRecipientDropdown, SenderRecipientOption } from "./SenderRecipientDropdown";

import type { SenderContext } from "../utils/resolveSenderContext.util";
import type { ListPerson } from "../../../schema/list";

interface DraftCardProps {
	draft: Draft;
	index: number;
	isActive: boolean;
	isCollapsed: boolean;
	totalDrafts: number;
	leadName: string;
	hasChannelMismatch: boolean;
	/** Whether the immediately preceding draft is a LinkedIn invite (connection request). */
	precedingIsInviteNote?: boolean;
	/**
	 * When set, the first draft is a LinkedIn invite and this is a follow-up: the
	 * value is the invite's `scheduledInDays` baseline. The send timing is then
	 * rendered as "N days later" relative to the invite, since the real send date
	 * depends on when the connection request is accepted.
	 */
	inviteBaselineDays?: number;
	/** The draft waits for the sender's pending invitation to be accepted (PREC-4216). */
	waitsForAcceptance?: boolean;
	sendFromOptions: SenderRecipientOption[];
	sendToOptions: SenderRecipientOption[];
	/** Current lead, used to resolve `{prospect_*}`/`{column_*}` tokens in the preview. */
	lead?: ListPerson;
	/** Available variable keys (from `getSuggestions`) used to resolve preview tokens. */
	suggestions?: string[];
	/** Resolved sender data ({signature}/{senderFirstName}/{senderLastName}) for this draft's sender. */
	senderContext?: SenderContext;
	/** The company's Include signature default, for a draft the user never toggled (PREC-4401). */
	signatureDefault?: boolean;
	/** Localized label for the `{unsubscribe_link}` chip. */
	unsubscribeLabel?: string;
	/**
	 * Portal target for the sender/recipient/timing dropdown menus. The Lead Panel
	 * passes its `<aside>` so a click in a menu counts as *inside* the panel's
	 * outside-click boundary (a `document.body` portal would close the panel);
	 * the extension passes its shadow root. Defaults to `document.body`.
	 */
	portalContainer?: HTMLElement | null;
	onClick: () => void;
	onDelete: () => void;
	onTimingChange: (timing: DraftTiming) => void;
	onFromChange: (option: SenderRecipientOption) => void;
	onToChange: (option: SenderRecipientOption) => void;
	onToggleCollapse: () => void;
}

export const DraftCard: FunctionComponent<DraftCardProps> = ({
	draft,
	index,
	isActive,
	isCollapsed,
	totalDrafts,
	leadName,
	hasChannelMismatch,
	precedingIsInviteNote,
	inviteBaselineDays,
	waitsForAcceptance,
	sendFromOptions,
	sendToOptions,
	lead,
	suggestions = [],
	senderContext = {},
	signatureDefault = false,
	unsubscribeLabel = "",
	portalContainer,
	onClick,
	onDelete,
	onTimingChange,
	onFromChange,
	onToChange,
	onToggleCollapse,
}) => {
	const { t } = useTranslation();
	const isFollowUp = index > 0;
	const isNow = draft.timing.value === "now";
	const isInviteNote = !!draft.isInviteNote;
	const isLinkedin = draft.from?.type === "linkedin";
	const charCount = draft.content.length;

	// Follow-up to a LinkedIn invite: show the send timing relative to the invite
	// ("N days later") instead of an absolute date the prospect-acceptance moves.
	const isInviteRelative = inviteBaselineDays !== undefined;

	const filteredSendFromOptions = useMemo(() => {
		if (isInviteNote) {
			return sendFromOptions.filter((o) => o.type === "linkedin");
		}

		return sendFromOptions;
	}, [sendFromOptions, isInviteNote]);

	return (
		<div className="mb-3 flex justify-end w-full">
			<div style={{ width: "80%" }}>
				{isFollowUp && (
					<div className="mt-10 mb-3 mr-1 flex justify-end">
						<p className="text-xs text-amber-600 flex items-center gap-1.5">
							<Icon className="w-3 h-3" icon={faClock} />
							This message will not be sent if {leadName} replies to the above message
							{precedingIsInviteNote ? " or does not accept the invite" : ""}.
						</p>
					</div>
				)}

				<div
					className={classNames(
						"cursor-pointer transition-all rounded-lg border-2 border-dashed w-full",
						hasChannelMismatch
							? "border-red-400 bg-red-50"
							: isActive
								? "border-blue-action bg-blue-action/10"
								: "border-blue-action/30 bg-blue-action/5 hover:border-blue-action/50"
					)}
					onClick={onClick}
				>
					{/* Header */}
					<div className="px-3 py-2 flex items-center gap-2 justify-between border-b border-blue-action/20 min-w-0 overflow-hidden">
						<div className="flex items-center gap-2 text-xs text-text-meta flex-wrap min-w-0 flex-1">
							{isInviteNote && (
								<Tooltip content="This message will be sent as a LinkedIn connection request">
									<span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold uppercase tracking-wide">
										<Icon className="w-3 h-3" icon={faUserPlus} />
										LinkedIn Invite
									</span>
								</Tooltip>
							)}
							{isFollowUp && !isInviteNote && (
								<span className="font-medium text-blue-action mr-1">Follow Up {index}</span>
							)}
							<span className="text-text-meta">Send from</span>
							<SenderRecipientDropdown
								options={filteredSendFromOptions}
								portalContainer={portalContainer}
								showLinkedinTier={isInviteNote}
								value={draft.from.label}
								onSelect={onFromChange}
							/>
							<span className="text-text-meta">to</span>
							<SenderRecipientDropdown
								options={sendToOptions}
								portalContainer={portalContainer}
								value={draft.to.label}
								onSelect={onToChange}
							/>
							{isInviteRelative || waitsForAcceptance ? (
								<span className="inline-flex items-center gap-1">
									<TimingDropdown
										afterAcceptance={waitsForAcceptance}
										portalContainer={portalContainer}
										relativeToInviteDays={waitsForAcceptance ? undefined : inviteBaselineDays}
										value={draft.timing}
										onSelect={onTimingChange}
									/>
									<Information
										iconClassName="w-3 h-3 text-text-meta"
										information={t("inbox.draft.acceptedConnectionTooltip")}
									/>
								</span>
							) : isNow ? (
								<TimingDropdown
									portalContainer={portalContainer}
									value={draft.timing}
									onSelect={onTimingChange}
								/>
							) : (
								<>
									<span className="text-text-meta">on</span>
									<TimingDropdown
										portalContainer={portalContainer}
										value={draft.timing}
										onSelect={onTimingChange}
									/>
								</>
							)}
						</div>
						{hasChannelMismatch && (
							<div className="flex items-center gap-1 text-red-500 mr-2 flex-shrink-0">
								<Icon className="w-3 h-3" icon={faTriangleExclamation} />
								<span className="text-xs font-medium">Channel mismatch</span>
							</div>
						)}
						{draft.reasoning && (
							<Tooltip
								panel
								className="flex-shrink-0"
								content={<ReplyReasoningPopover reasoning={draft.reasoning} />}
							>
								<button
									type="button"
									className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold text-blue-action cursor-pointer hover:bg-blue-action/10"
									onClick={(e) => e.stopPropagation()}
								>
									<Icon className="w-3 h-3" icon={faCircleQuestion} />
									{t("inbox.draft.whyThis")}
								</button>
							</Tooltip>
						)}
						<div className="flex items-center gap-1 flex-shrink-0">
							<button
								className="p-1 hover:bg-blue-action/10 cursor-pointer rounded text-blue-action"
								onClick={(e) => {
									e.stopPropagation();
									onToggleCollapse();
								}}
							>
								<Icon className="w-3 h-3" icon={isCollapsed ? faChevronDown : faChevronUp} />
							</button>
							{totalDrafts > 1 && (
								<button
									className="p-1 hover:bg-red-100 cursor-pointer rounded text-red-400 hover:text-red-500"
									onClick={(e) => {
										e.stopPropagation();
										onDelete();
									}}
								>
									<Icon className="w-3 h-3" icon={faTrash} />
								</button>
							)}
						</div>
					</div>

					{/* Message content */}
					{!isCollapsed && (
						<div className="px-3 py-3">
							{draft.subject && (
								<p className="text-xs text-text-meta mb-1">
									<span className="font-medium">Subject:</span>{" "}
									<span
										dangerouslySetInnerHTML={{
											__html: resolveDraftPreviewHtml(
												draft.subject,
												lead,
												suggestions,
												senderContext,
												unsubscribeLabel,
												{ isLinkedin }
											),
										}}
									/>
								</p>
							)}
							{draft.content ? (
								<div
									className="text-sm text-text break-words max-h-[240px] overflow-y-auto"
									dangerouslySetInnerHTML={{
										__html: resolveDraftPreviewHtml(
											draft.content,
											lead,
											suggestions,
											senderContext,
											unsubscribeLabel,
											{
												isLinkedin,
												includeSignature:
													!isLinkedin && (draft.includeSignature ?? signatureDefault),
											}
										),
									}}
								/>
							) : (
								<p className="text-sm text-text">
									<span className="text-text-meta italic">{isActive ? "" : "Empty draft..."}</span>
								</p>
							)}
							{isInviteNote && (
								<div className="mt-1.5 flex justify-end">
									<InviteNoteCharCounter
										charCount={charCount}
										tier={resolveSenderTier(draft.from.userId, sendFromOptions)}
									/>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
